import { AlertType } from './AlertType.enum';

export enum AlertSubtype {
  AccountCreated = 'Account Created',
  AllocationNeedsAttention = 'Allocation Needs Attention',
  AnomalousUsage = 'Anomalous Usage',
  BillProcessed = 'Bill Processed',
  CustomerAccountCreated = 'Customer Account Created',
  CustomerAccountLinked = 'Customer Account Linked',
  ErrorInBillImport = 'Error In Bill Import',
  FileIngested = 'File Ingested',
  InsufficientQuality = 'Insufficient Quality',
  InvalidCredentials = 'Invalid Credentials',
  MeterChangeout = 'Meter Changeout',
  MeterCreated = 'Meter Created',
  PropertyCreated = 'Property Created',
  PropertyLinked = 'Property Linked',
  ReportAvailable = 'Report Available',
  StatementRequiresReview = 'Statement Requires Review',
  UrgentDataInsights = 'Urgent Data Insights',
}

export const hiddenAlertSubtypes: Set<AlertSubtype> = new Set([
  AlertSubtype.AnomalousUsage,
  AlertSubtype.BillProcessed,
  AlertSubtype.MeterChangeout,
]);

export const typeToSubtypeAlertMap: Record<AlertType, AlertSubtype[]> = {
  [AlertType.Allocations]: [AlertSubtype.AllocationNeedsAttention],
  [AlertType.BillAutomation]: [
    AlertSubtype.AccountCreated,
    AlertSubtype.BillProcessed,
    AlertSubtype.InsufficientQuality,
    AlertSubtype.InvalidCredentials,
    AlertSubtype.MeterCreated,
    AlertSubtype.MeterChangeout,
    AlertSubtype.StatementRequiresReview,
  ],
  [AlertType.DataInsights]: [AlertSubtype.UrgentDataInsights],
  [AlertType.DataIngest]: [
    AlertSubtype.ErrorInBillImport,
    AlertSubtype.FileIngested,
  ],
  [AlertType.EnergyStar]: [
    AlertSubtype.CustomerAccountCreated,
    AlertSubtype.CustomerAccountLinked,
    AlertSubtype.PropertyCreated,
    AlertSubtype.PropertyLinked,
    AlertSubtype.MeterCreated,
  ],
  [AlertType.IntervalData]: [AlertSubtype.AnomalousUsage],
  [AlertType.Reporting]: [AlertSubtype.ReportAvailable],
};

export const visibleTypeToSubtypeAlertMap: Record<AlertType, AlertSubtype[]> =
  Object.fromEntries(
    (Object.entries(typeToSubtypeAlertMap) as [AlertType, AlertSubtype[]][])
      .map(([type, subtypes]) => [
        type,
        subtypes.filter((subtype) => !hiddenAlertSubtypes.has(subtype)),
      ])
      .filter(([, subtypes]) => subtypes.length > 0),
  ) as Record<AlertType, AlertSubtype[]>;

export const requireResolutionSubtypes: AlertSubtype[] = [
  AlertSubtype.AllocationNeedsAttention,
  AlertSubtype.StatementRequiresReview,
  AlertSubtype.InsufficientQuality,
  AlertSubtype.InvalidCredentials,
  AlertSubtype.AnomalousUsage,
  AlertSubtype.UrgentDataInsights,
];

export const infoOnlySubtypes: AlertSubtype[] = [
  AlertSubtype.AccountCreated,
  AlertSubtype.ErrorInBillImport,
  AlertSubtype.CustomerAccountCreated,
  AlertSubtype.CustomerAccountLinked,
  AlertSubtype.FileIngested,
  AlertSubtype.PropertyCreated,
  AlertSubtype.PropertyLinked,
  AlertSubtype.MeterCreated,
  AlertSubtype.MeterChangeout,
  AlertSubtype.ReportAvailable,
];

export const defaultAlertResolutionMap: Partial<Record<AlertSubtype, boolean>> =
  { [AlertSubtype.BillProcessed]: true };

export const subtypeDescriptions: Record<AlertSubtype, string> = {
  [AlertSubtype.AccountCreated]:
    'Receive a notification when a new utility account is created during bill processing. Useful for tracking newly discovered accounts.',
  [AlertSubtype.AllocationNeedsAttention]:
    'Receive a notification when allocation logic needs manual review or correction. Helps you quickly resolve issues before allocations impact downstream reporting.',
  [AlertSubtype.AnomalousUsage]:
    'Receive a notification when interval data suggests unusual usage behavior. Supports faster investigation of spikes, drops, and potential operational issues.',
  [AlertSubtype.BillProcessed]:
    'Receive a notification when bills for selected accounts are successfully processed. Good for teams that monitor processing completion and expected billing cadence.',
  [AlertSubtype.CustomerAccountCreated]:
    'Receive a notification when an ENERGY STAR customer account is created. Keeps your team aligned on setup milestones and account readiness.',
  [AlertSubtype.CustomerAccountLinked]:
    'Receive a notification when an ENERGY STAR account link is completed. Helpful for validating account integrations and setup status.',
  [AlertSubtype.ErrorInBillImport]:
    'Receive a notification when a bill import fails or returns an error. Lets you intervene quickly so billing pipelines stay current.',
  [AlertSubtype.FileIngested]:
    'Receive a notification when a data file is successfully ingested. Useful for confirming source file delivery and processing continuity.',
  [AlertSubtype.InsufficientQuality]:
    'Receive a notification when uploaded statement quality is too low for automation. Helps your team remediate image quality before processing stalls.',
  [AlertSubtype.InvalidCredentials]:
    'Receive a notification when an integration credential is no longer valid. Enables quick credential updates to avoid data collection interruptions.',
  [AlertSubtype.MeterChangeout]:
    'Receive a notification when a meter changeout is detected in source data. Supports continuity checks in consumption trends and meter histories.',
  [AlertSubtype.MeterCreated]:
    'Receive a notification when a new meter record is created during bill processing. Useful for tracking newly discovered meters that may require proper association with a facility.',
  [AlertSubtype.PropertyCreated]:
    'Receive a notification when a new ENERGY STAR property is created. Keeps stakeholders aware of property onboarding progress.',
  [AlertSubtype.PropertyLinked]:
    'Receive a notification when an existing ENERGY STAR property is linked. Keeps stakeholders aware of property onboarding progress.',
  [AlertSubtype.ReportAvailable]:
    'Receive a notification when a requested report is generated and ready. Useful for teams that distribute or review reports on a schedule.',
  [AlertSubtype.StatementRequiresReview]:
    'Receive a notification when a statement is unable to be processed and requires review. Helps ensure statements that we are unable to process are promptly identified for review.',
  [AlertSubtype.UrgentDataInsights]:
    'Receive a notification for urgent data insight findings requiring attention. Ideal when you want high-priority insights surfaced as soon as possible.',
};
